import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import PhoneMockup from "@/components/PhoneMockup";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

const rotatingWords = [
  "Coaches",
  "Hair Stylists",
  "Dental Offices",
  "Fitness Studios",
  "Home Services",
  "Car Dealerships",
  "Cleaning Companies",
  "HVAC",
];

export default function HeroSection() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle at center, rgba(59, 130, 246, 0.06) 0%, transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-left"
          >
            <h1
              data-testid="hero-heading"
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] tracking-tight mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Your 24/7 Appointment Setter for{" "}
              <span className="inline-block relative h-[1.15em] align-bottom overflow-hidden" style={{ minWidth: "520px" }}>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={rotatingWords[index]}
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -40, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="absolute left-0 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent whitespace-nowrap"
                  >
                    {rotatingWords[index]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>

            <p data-testid="hero-subheading" className="text-base md:text-lg text-zinc-400 max-w-lg mb-10 leading-relaxed">
              We build an AI that qualifies your leads, handles follow up, and books calls into your calendar — built from scratch around your business. Not a template.
            </p>

            <Button
              data-testid="hero-cta-primary"
              onClick={() => window.location.href = "/apply.html"}
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-6 text-base font-bold transition-all duration-200 hover:scale-105 shadow-[0_0_30px_rgba(59,130,246,0.4)] flex items-center gap-2"
            >
              Book Your Free Strategy Call <ArrowRight size={18} />
            </Button>

            <p className="text-zinc-600 text-xs mt-4 max-w-sm">
              Free call. No commitment. We'll show you exactly what we'd build for your business.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="flex justify-center lg:justify-end"
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
